import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
    cloneDeep,
    compact,
    find,
    flatten,
    groupBy,
    map,
    merge,
    noop,
    omit,
    pickBy,
    isEmpty,
    trim,
    without,
    isEqual,
    isFunction,
} from 'lodash';
import { t } from '@lingui/macro';

import PanelRegistry from '../panel/PanelRegistry';
import { useDashboardService } from '../../../services/DashboardService';
import useSideNavState from '../../../stores/SideNavStateStore';
import { useShouldFloat } from '../../side-nav/management/MenuContextProvider';
import { useLastLocationUrl } from '../../side-nav/SidebarLocationHistory';
import { DashboardMenuStoreContainer, useMenu } from '../../../stores/DashboardMenuStore';
import { useDashboardMenuService } from '../../../services/DashboardMenuService';
import { useFilterState } from '../../../stores/FilterStateStore';
import { useUser, useIsSysAdminUser, useIsAdminUser } from '../../../stores/SessionStore';
import { useNavigate } from '../../../utilities/PgmUtilities';
import { pixelsToRows, DEFAULT_DROPPING_ITEM } from '../panel-container/UseLayoutReducer';
import { makeSavedFiltersName } from '../filter-container/SaveFiltersDialog';

export const DASHBOARD_ROW_HEIGHT = 20;
export const DASHBOARD_ID_PATTERN = /^([0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12})/i;
export const NEW_DASHBOARD_ID = 'new-dashboard';
export const NEW_DASHBOARD_TITLE = 'New Dashboard';

const COLUMN_CLASS_WIDTH_MAP = {
    'col-full': 12,
    'col-full-flex-height': 12,
    'col-2-thirds': 8,
    'col-half': 6,
    'col-1-third': 4,
    'col-1-quarter': 3,
};

const DEFAULT_STATE = {
    error: undefined,
    titleError: undefined,
    loading: true,
    editing: false,
    dragging: false,
    droppingItem: DEFAULT_DROPPING_ITEM,
    editable: false,
    deletable: false,
    sharable: false,
    valid: false,
    dashboard: undefined,
    dirtyDashboard: undefined,
    dashboardActions: {
        load: noop,
        save: noop,
        reset: noop,
        setEditing: noop,
        setDragging: noop,
        setDroppingItemHeight: noop,
        setTitle: noop,
        setPanels: noop,
        setPanelOverrides: noop,
    },
};

export function isDashboard(pageId) {
    return pageId === NEW_DASHBOARD_ID || DASHBOARD_ID_PATTERN.test(pageId);
}

function makeTitleUnique(titleToMakeUnique, dashboardMenu) {
    const existingDashboard = dashboardMenu?.privateDashboards.find((dash) => dash.title === titleToMakeUnique);

    if (existingDashboard) {
        const titleCountRegex = new RegExp('(.+?)(\\d+)');
        const match = titleCountRegex.exec(titleToMakeUnique);
        let count = 0;

        if (match) {
            titleToMakeUnique = match[1].trim();
            count = parseInt(match[2], 10);
        }

        return makeTitleUnique(`${titleToMakeUnique} ${count + 1}`, dashboardMenu);
    }

    return titleToMakeUnique;
}

/**
 * Transforms the filters from the format used in the PanelRegistry to the format
 * needed by the DashboardContext.
 */
function transformFilters(filters, exclude) {
    return [
        ...(without(filters.required, ...exclude).map((id) => ({ id, required: true })) || []),
        ...(without(filters.optional, ...exclude).map((id) => ({ id, required: false })) || []),
    ];
}

/**
 * Gets the filter requirements for the specified panel.
 */
function getPanelFilters(panel) {
    const overriddenFilters = Object.keys(panel.overrides?.filters || {});
    return transformFilters(PanelRegistry.getFiltersForPanelType(panel.id), overriddenFilters);
}

/**
 * Reduces the collection of filters needed by the panels, which may have duplicates,
 * into a set.
 */
function reduceFilters(filters) {
    return filters.reduce((mergedFilter, filter) => {
        return merge(
            mergedFilter,
            pickBy(filter, (value, key) => {
                // Pick all properties that are not the 'required' property.
                if (key !== 'required') return true;

                // Only pick the 'required' property if its value is true.  This ensures required filters are given
                // priority over optional filters when merging properties.
                return value;
            })
        );
    }, {});
}

/**
 * Resolves the set of filters for the specified panels.
 */
function resolveFilters(panels) {
    const panelFilters = flatten(compact(panels.map((panel) => getPanelFilters(panel))));
    const filtersById = groupBy(panelFilters, 'id');
    return map(filtersById, reduceFilters);
}

const DashboardContext = createContext();

export function useDashboardContext() {
    // The context is undefined in PGM pages, so guard against that by returning the
    // default state if there is no context.
    return useContext(DashboardContext) || DEFAULT_STATE;
}

export function useDashboardContextValue(dashboardService) {
    const navigate = useNavigate();
    const [user] = useUser();
    const [isSysAdminUser] = useIsSysAdminUser();
    const [isAdminUser] = useIsAdminUser();
    const shouldFloat = useShouldFloat();
    const lastLocationUrl = useLastLocationUrl();
    const [, /* Not used */ sideNavStateActions] = useSideNavState();
    const [dashboardMenu, dashboardMenuActions] = useMenu();
    const [filterState] = useFilterState();
    const [state, setState] = useState({
        ...DEFAULT_STATE,
    });

    /**
     * Validates the dashboard and returns the `valid` boolean and a `titleError`, if any.
     */
    const validate = useCallback((dashboard) => {
        const trimmedTitle = trim(dashboard.title);
        const hasTitle = !isEmpty(trimmedTitle);
        const hasPanels = dashboard.panels.length;
        const valid = hasTitle && hasPanels;

        let titleError;
        if (!hasTitle) {
            titleError = t`A dashboard title is required`;
        }

        return {
            valid,
            titleError,
        };
    }, []);

    /**
     * Resets the state back to the default.
     */
    const reset = useCallback(() => {
        setState({
            ...DEFAULT_STATE,
        });
    }, []);

    const share = useCallback(
        (id, shareBody) => {
            return dashboardService
                .share(id, shareBody)
                .then((sharedDashboard) => {
                    const sharedDashboardState = {
                        ...sharedDashboard,
                        groups: shareBody.private ? null : shareBody.groups,
                    };

                    setState((currentState) => ({
                        ...currentState,
                        dashboard: sharedDashboardState,
                        dirtyDashboard: cloneDeep(sharedDashboardState),
                    }));
                })
                .catch((error) => {
                    setState((currentState) => ({
                        ...currentState,
                        error,
                    }));
                    throw error; // Rethrow so the caller can handle it
                });
        },
        [dashboardService]
    );

    /**
     * Loads a dashboard and optionally sets the editing state.
     */
    const load = useCallback(
        (id, editing = false) => {
            const isNew = id === NEW_DASHBOARD_ID;

            setState((currentState) => ({
                ...currentState,
                loading: true,
            }));

            dashboardService
                .get(id)
                .then((loadedDashboard) => {
                    const dashboard = {
                        ...loadedDashboard,
                        id: isNew ? NEW_DASHBOARD_ID : loadedDashboard._id,
                        title: isNew ? makeTitleUnique(NEW_DASHBOARD_TITLE, dashboardMenu) : loadedDashboard.title,
                        filters: resolveFilters(loadedDashboard.panels),
                        panels: loadedDashboard.panels.map((panel, index) => ({
                            ...panel,
                            // Add a dashboard ID so we have a unique identifier for the panel while editing.
                            dashboardId: `${panel.id}-${index}`,
                            // Make sure legacy dashboard panels have a layout property by converting the legacy
                            // `columnClass` property to the layout equivalent.
                            layout: panel.layout || {
                                w: COLUMN_CLASS_WIDTH_MAP[panel.columnClass] || 12,
                            },
                        })),
                    };

                    const ownedByCurrentUser = dashboard.userId === user?.id;
                    const editable = ownedByCurrentUser;
                    const deletable = ownedByCurrentUser || isSysAdminUser;
                    const sharable = ownedByCurrentUser && (isSysAdminUser || isAdminUser);

                    setState((currentState) => ({
                        ...currentState,
                        loading: false,
                        editing: editing || isNew,
                        editable,
                        deletable,
                        sharable,
                        dashboard,
                        dirtyDashboard: cloneDeep(dashboard),
                    }));
                })
                .catch((error) => {
                    setState({
                        ...DEFAULT_STATE,
                        error,
                    });
                });
        },
        [dashboardMenu, user, isSysAdminUser, isAdminUser, dashboardService]
    );

    /**
     * Saves the dashboard and clears the editing state.
     */
    const save = useCallback(() => {
        // Omit the temporary props added to the dashboard and its panels before saving.
        const dashboardToSave = merge(
            {
                ...omit(state.dirtyDashboard, ['id', 'filters', 'savedFilterState']),
                panels: state.dirtyDashboard.panels.map((panel) => omit(panel, 'dashboardId')),
            },
            isEmpty(filterState?.encoded)
                ? {}
                : {
                      savedFilterState: {
                          name: makeSavedFiltersName(filterState),
                          encodedFilterState: filterState?.encoded,
                      },
                  }
        );

        setState((currentState) => ({
            ...currentState,
            saving: true,
        }));

        dashboardService.save(dashboardToSave).then(async (savedDashboard) => {
            // We need to reload the dashboard menu so that the dashboard title is kept
            // up to date with changes, and so new dashboards show up in the menu.
            await dashboardMenuActions.load();

            if (savedDashboard._id !== state.dashboard._id) {
                // This was a new dashboard that didn't have an ID before it was saved.
                // We need to navigate to the new URL for the dashboard.
                navigate(`/${savedDashboard._id}${window.location.search}`, { replace: true });
            } else {
                // Filters aren't saved in the dashboard, so we need to resolve them again
                // before updating the state.
                const dashboard = {
                    ...savedDashboard,
                    id: savedDashboard._id,
                    filters: resolveFilters(savedDashboard.panels),
                    panels: savedDashboard.panels.map((panel, index) => ({
                        ...panel,
                        // Add a dashboard ID so we have a unique identifier for the panel while editing.
                        dashboardId: `${panel.id}-${index}`,
                    })),
                };

                // This was an existing dashboard, so no need to navigate.  Just clear
                // the editing flag and update the dashboard state.
                setState((currentState) => ({
                    ...currentState,
                    editing: false,
                    saving: false,
                    dashboard,
                    dirtyDashboard: cloneDeep(dashboard),
                }));
            }
        });
    }, [state, filterState, dashboardService, dashboardMenuActions, navigate]);

    /**
     * Sets the editing state.
     */
    const setEditing = useCallback(
        (editing) => {
            let isNew = false;

            setState((currentState) => {
                const clonedDashboard = cloneDeep(currentState.dashboard);

                isNew = currentState.dashboard?.id === NEW_DASHBOARD_ID;

                return {
                    ...currentState,
                    editing,
                    dashboard: !editing ? clonedDashboard : currentState.dashboard,
                    dirtyDashboard: !editing ? clonedDashboard : currentState.dirtyDashboard,
                };
            });

            sideNavStateActions.setFloating(editing ? false : shouldFloat);

            // Delay navigation until the next tick to allow the state change to be applied.
            if (!editing && isNew) setTimeout(() => navigate(lastLocationUrl), 0);
        },
        [sideNavStateActions, navigate, lastLocationUrl, shouldFloat]
    );

    /**
     * Sets the dragging state.  Used to indicate when either a new panel or an existing panel
     * is dragged over the dashboard.
     */
    const setDragging = useCallback((dragging) => {
        setState((currentState) => ({
            ...currentState,
            dragging,
        }));
    }, []);

    /**
     * Sets the height for the dropping item.  Used while dragging so that the drop placeholder
     * has an appropriate height for the dragged panel.
     */
    const setDroppingItemHeight = useCallback((height) => {
        setState((currentState) => ({
            ...currentState,
            droppingItem: {
                ...DEFAULT_DROPPING_ITEM,
                h: pixelsToRows(height || DEFAULT_DROPPING_ITEM.h, DASHBOARD_ROW_HEIGHT),
            },
        }));
    }, []);

    /**
     * Sets the dashboard title.
     */
    const setTitle = useCallback(
        (title) => {
            setState((currentState) => {
                const dirtyDashboard = {
                    ...currentState.dirtyDashboard,
                    title,
                };

                return {
                    ...currentState,
                    ...validate(dirtyDashboard),
                    dirtyDashboard,
                };
            });
        },
        [validate]
    );

    /**
     * Sets the panels in the dashboard and updates the filters for the new panel set.
     */
    const setPanels = useCallback(
        (newPanels) => {
            setState((currentState) => {
                const panels = newPanels.map((newPanel) => {
                    const existingPanel = find(
                        currentState.dirtyDashboard.panels,
                        (panel) => panel.dashboardId === newPanel.dashboardId
                    );
                    return existingPanel
                        ? {
                              ...newPanel,
                              // We need to keep any edited state for the existing panel.
                              overrides: existingPanel.overrides,
                              hideTitle: existingPanel.hideTitle,
                          }
                        : newPanel;
                });
                const dirtyDashboard = {
                    ...currentState.dirtyDashboard,
                    filters: resolveFilters(panels),
                    panels: cloneDeep(panels),
                };

                return {
                    ...currentState,
                    ...validate(dirtyDashboard),
                    dirtyDashboard,
                };
            });
        },
        [validate]
    );

    const setPanelOverrides = useCallback((dashboardId, panelOverrides) => {
        setState((currentState) => {
            const panel = currentState.dirtyDashboard?.panels.find((p) => p.dashboardId === dashboardId);
            const currentPanelOverrides = panel?.overrides;
            const newPanelOverrides = isFunction(panelOverrides)
                ? panelOverrides(currentPanelOverrides)
                : panelOverrides;

            // If the panel overrides didn't really change, don't update the state.
            if (isEqual(currentPanelOverrides, newPanelOverrides)) return currentState;

            const panels =
                currentState.dirtyDashboard?.panels.map((p) => {
                    if (p.dashboardId === dashboardId) {
                        return isEmpty(newPanelOverrides)
                            ? omit(p, 'overrides')
                            : {
                                  ...p,
                                  overrides: cloneDeep(newPanelOverrides),
                              };
                    }

                    return p;
                }) || [];
            const dirtyDashboard = {
                ...currentState.dirtyDashboard,
                // Ensure filter overrides are reflected in the dashboard filter set.
                filters: resolveFilters(panels),
                panels,
            };

            return {
                ...currentState,
                dirtyDashboard,
            };
        });
    }, []);

    const dashboardActions = useMemo(
        () => ({
            load,
            save,
            reset,
            setEditing,
            setDragging,
            setDroppingItemHeight,
            setTitle,
            setPanels,
            setPanelOverrides,
            share,
        }),
        [
            load,
            save,
            reset,
            setEditing,
            setDragging,
            setDroppingItemHeight,
            setTitle,
            setPanels,
            setPanelOverrides,
            share,
        ]
    );

    return useMemo(() => {
        return {
            ...state,
            dashboardActions,
        };
    }, [state, dashboardActions]);
}

function DashboardProviderInner({ children, dashboardService }) {
    const dashboardContextValue = useDashboardContextValue(dashboardService);

    return <DashboardContext.Provider value={dashboardContextValue}>{children}</DashboardContext.Provider>;
}

export default function DashboardProvider({ children }) {
    const dashboardService = useDashboardService();
    const dashboardMenuService = useDashboardMenuService();

    return (
        <DashboardMenuStoreContainer dashboardService={dashboardService} dashboardMenuService={dashboardMenuService}>
            <DashboardProviderInner dashboardService={dashboardService}>{children}</DashboardProviderInner>
        </DashboardMenuStoreContainer>
    );
}
